import { ApolloCache } from '@apollo/client/cache';
import { DocumentNode, FieldPolicy } from '@apollo/client/core';
import { ApolloError } from '@apollo/client/errors';
import { DefinitionNode, FieldNode, OperationDefinitionNode, visit } from 'graphql';
import { produce } from 'immer';

import { Edge, Node } from '@hdms/shared/common-types';
import { EnvironmentService } from '@hdms/shared/util-core';

export function getFirstOperationDefinition(definitions: readonly DefinitionNode[]): OperationDefinitionNode {
  return (definitions as OperationDefinitionNode[])[0];
}

export function getQueryName({ definitions }: DocumentNode): string {
  return getFirstOperationDefinition(definitions).name?.value;
}

export function isQueryOperation({ definitions }: DocumentNode): boolean {
  return getFirstOperationDefinition(definitions).operation === 'query';
}

export function getQueryFieldNodes({ definitions }: DocumentNode): readonly FieldNode[] {
  return getFirstOperationDefinition(definitions).selectionSet.selections as FieldNode[];
}

export function getQueryFieldNames(documentNode: DocumentNode): readonly string[] {
  return getQueryFieldNodes(documentNode).map(({ name }) => name.value);
}

export type UpdateApolloCacheFn<T> = (data: T) => T;

export const ID_FIELD = 'id';

/**
 * Helper for updating previously queried data in Apollo cache
 *
 * Usage:
 *
 * const query: DocumentNode = someGqlQueryDocument;
 * const variables: GqlQueryArgs = { input: {} };
 * const cache: InMemoryCache = this.apollo.client.cache;
 * - or when used inside update callback of mutation:
 *   use the provided cache: ApolloCache<any> argument
 *
 * updateCachedQuery<GqlQueryResponse>(
 *   cache,
 *   query,
 *   data => {
 *     data.someValueToChange = newValue;
 *     return data;
 *   },
 *   variables
 * )
 *
 * @param cache
 * @param query
 * @param updateFn
 * @param variables
 *
 * @returns the updated data object from cache
 */

export function updateCachedQuery<T>(
  cache: ApolloCache<any>,
  query: DocumentNode,
  updateFn: UpdateApolloCacheFn<T>,
  variables: Record<string, any> = null
): T {
  const queryConfig = {
    query,
    variables
  };

  try {
    const queryResults = cache.readQuery(queryConfig);

    // in case query fields have a merge method configured, then in order
    // to avoid duplications after subsequent field policy merge invocation,
    // evict related root-query fields before re-adding them via cache.writeQuery.
    const policyFields = (cache as any).policies.typePolicies.Query.fields;
    const mergePolicyFields = Object.keys(policyFields).filter(key => policyFields[key].merge);
    getQueryFieldNames(query)
      .filter(name => mergePolicyFields.includes(name))
      .forEach(fieldName =>
        cache.evict({
          fieldName,
          broadcast: false
        })
      );

    // alter immutable data from Apollo InMemoryCache using immer.js produce()
    const data = produce(queryResults, draft => updateFn(draft as T)) as T;

    cache.writeQuery({ ...queryConfig, data });

    return data;
  } catch (e) {
    return null;
  }
}

export const GQL_SESSION_EXPIRED = 'session expired';

/**
 * returns true if a GraphQL error is caused by session expiration
 * @param error
 */
export function isGraphQLSessionExpiredError(error: any): boolean {
  return error?.toString().toLowerCase().includes(GQL_SESSION_EXPIRED);
}

/**
 * returns true if a GraphQL error is caused by mothballer endpoint hibernation
 * @param error
 */
export function isGraphQLMothballerError(error: any): boolean {
  return error?.toString().toLowerCase().includes('mothballer');
}

/**
 * returns true if a GraphQL error is caused by invalid CSRF token
 * @param error
 */
export function isGraphQLInvalidTokenError(error: any): boolean {
  return error?.toString().toLowerCase().includes('invalid csrf token');
}

/**
 * This function checks to see if the errors returned from graphql
 * has the error code enum in the array of errors.
 * @param  {The full error returned from GraphQL} error
 * @param  {The enum value of graphQLError.extensions.code} errCode
 */
export function isGraphqlError(error: ApolloError, errCode: string): boolean {
  const errorsList = error?.graphQLErrors || [];
  return errorsList.map(({ extensions }) => extensions.code).some(code => code === errCode);
}

/**
 * Removes certain fields from a GraphQL document / AST
 * Limitation: fields cannot be defined depending on their hierarchical location inside the AST
 * hence any field matching the field name will be removed from AST
 * @param query
 * @param fieldsToRemove
 */
export function removeFieldsFromDocument(query: DocumentNode, fieldsToRemove: ReadonlyArray<string>): DocumentNode {
  return visit(query, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Field: {
      enter(node: any) {
        return fieldsToRemove.includes(node.name.value) ? null : undefined;
      }
    }
  });
}

export const CCMSV2_PREFIX = 'CCMSv2';

/**
 * Adds `CCMSV2` prefix to operation name
 * @param query
 */
export function addCCMSV2OperationNamePrefix(query: DocumentNode): DocumentNode {
  return visit(query, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    OperationDefinition: {
      enter(node: any) {
        return {
          ...node,
          name: {
            ...node.name,
            value: CCMSV2_PREFIX + node.name.value
          }
        };
      }
    }
  });
}

/**
 * Custom Limited Offset Pagination helper function to be used in InMemoryCache typePolicies to merge results of fetchMore calls
 * @param keyArgs - if it's of type Array then `pagination.pageSize` key will automatically be added to it
 * @param keyField - optional, for entities which don't have "id" property, but e.g. "originId" instead.
 * TODO: remove @param keyField when the CCMS migration is fully finalized.
 */
export function pageBasedPagination(
  keyArgs: FieldPolicy['keyArgs'] = false,
  keyField: string = null
): FieldPolicy<{ results: any[] }> {
  return {
    keyArgs: stringifyKeyArgs(keyArgs, args => ({ pageSize: args.pagination.pageSize })),
    merge: ({ results: existingResults } = { results: [] }, incoming, { readField }) => {
      const { results: incomingResults } = incoming;
      // For identifying items don't rely on the cached item `__ref` property as it could be, that for the field type
      // the field policy `keyFields` is set to false, leading to not normalizing items but keeping the actual data set.
      // Hence, better use `readField('id', item)` to get a unique item identifier we can use for filtering etc.
      // This approach only requires each item to have an `id` field, as it should be by GraphQL convention.
      const existingRefs = existingResults.map(item => readField(keyField || ID_FIELD, item));
      // filter duplicates in subsequently fetched items which were created before and added via cache update
      const newIncoming = incomingResults.filter(item => !existingRefs.includes(readField(keyField || ID_FIELD, item)));

      return {
        ...incoming,
        results: [...existingResults, ...newIncoming]
      };
    }
  };
}

/**
 * Custom Cursor Based Pagination helper function to be used in InMemoryCache typePolicies to merge results of fetchMore calls
 * @param keyArgs - if it's of type Array then `pagination.first` key will automatically be added to it
 */
export function cursorBasedPagination(keyArgs: FieldPolicy['keyArgs'] = false): FieldPolicy<{ edges: { node: {} }[] }> {
  return {
    keyArgs: stringifyKeyArgs(keyArgs, args => ({ first: args.pagination.first })),
    merge: ({ edges: existingResults } = { edges: [] }, incoming, { readField }) => {
      const { edges: incomingResults } = incoming;
      const existingRefs = existingResults.map(item => readField(ID_FIELD, item.node));
      const newIncoming = incomingResults.filter(item => !existingRefs.includes(readField(ID_FIELD, item.node)));

      return {
        ...incoming,
        edges: [...existingResults, ...newIncoming]
      };
    }
  };
}

function stringifyKeyArgs(
  keyArgs: FieldPolicy['keyArgs'],
  defaultArgsFn: (args: Record<string, any>) => Record<string, any>
): FieldPolicy['keyArgs'] {
  if (Array.isArray(keyArgs)) {
    const keys = keyArgs as string[];
    keyArgs = args => JSON.stringify(keys.reduce((acc, curr) => ({ ...acc, [curr]: args[curr] }), defaultArgsFn(args)));
  }

  return keyArgs;
}

export interface Localization {
  country: string;
  language: string;
}
/**
 * Helper to conveniently return required LocalizationInput properties from EnvironmentService
 * @param EnvironmentService
 */
export function getLocalization({ country, language }: EnvironmentService): Localization {
  return {
    country,
    language
  };
}

export function mapEdgesToNodes(edges: Edge[]): Node[] {
  return edges.map(edge => edge.node);
}

export function omitTypename<O extends { __typename?: string }>(item: O): Omit<O, '__typename'> {
  const { __typename, ...props } = item;
  return props;
}
